import { Component, OnInit, OnDestroy, isDevMode, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule, Router, NavigationEnd } from '@angular/router';

import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ThemeService } from './services/theme.service';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AuthService } from './services/auth.service';
import { AdminService } from './services/admin.service';
import { LoaderComponent } from './components/loader/loader.component';
import { PullToRefreshComponent } from './components/pull-to-refresh/pull-to-refresh.component';
import { RefreshService } from './services/refresh.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterModule,
    ButtonModule,
    MenuModule,
    DialogModule,
    ToastModule,
    LoaderComponent,
    PullToRefreshComponent
],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  title = 'Jagannath Temple';
  isAuthenticated = false;
  isAdmin = false;
  currentRoute = '';

  // Add properties for the update dialog
  showUpdateDialog = false;
  currentVersion = '';
  newVersion = '';

  private refreshSubscription: Subscription | null = null;

  menuItems = [
    {
      label: 'Settings',
      icon: 'pi pi-cog',
      command: () => {
        this.router.navigate(['/settings']);
      }
    },
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: async () => await this.onLogout()
    }
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private adminService: AdminService,
    private swUpdate: SwUpdate,
    private refreshService: RefreshService,
    _themeService: ThemeService
  ) {
    this.authService.currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.isAuthenticated = !!user;
        this.isAdmin = this.adminService.isAdmin();
      });

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.currentRoute = event.urlAfterRedirects.split('?')[0].replace(/^#/, '');
        }
      });
  }

  isActiveRoute(path: string): boolean {
    return this.currentRoute === path || this.currentRoute.startsWith(path + '/');
  }

  ngOnInit(): void {
    if (!isDevMode() && this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe(evt => {

          // Instead of using confirm(), store version info and show dialog
          this.currentVersion = evt.currentVersion.hash;
          this.newVersion = evt.latestVersion.hash;
          this.showUpdateDialog = true;
        });
    }

    // Subscribe to refresh events
    this.refreshSubscription = this.refreshService.refresh$.subscribe(route => {
      this.handleRefresh(route);
    });
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  private handleRefresh(route: string): void {
    // Determine which component to refresh based on the current route
    if (route.startsWith('/bookings')) {
      // Force route reload by navigating to the same route
      // This will refresh the component's data
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([route]);
      });
    } else if (route.startsWith('/dashboard')) {
      // Force route reload
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([route]);
      });
    } else {
      // For other routes, simply navigate to the same route to refresh
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([route]);
      });
    }
  }

  updateApplication(): void {
    this.showUpdateDialog = false;
    document.location.reload();
  }

  async onLogout() {
    await this.authService.signOut();
    this.router.navigate(['/login']);
  }
}
